/**
 * useBlogEditor Hook
 * 
 * Custom hook for managing blog post editing state and operations.
 * Handles create, update, and delete operations.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminBlogService } from '../services/adminBlogService';
import type { BlogPostInput } from '../types/admin.types';
import type { BlogPost } from '../types/blog.types';

/**
 * Hook return type
 */
export interface UseBlogEditorReturn {
  /** Current blog post data */
  blog: BlogPostInput;
  /** Update blog post data */
  setBlog: React.Dispatch<React.SetStateAction<BlogPostInput>>;
  /** Update a specific field */
  updateField: <K extends keyof BlogPostInput>(
    field: K,
    value: BlogPostInput[K]
  ) => void;
  /** Whether save operation is in progress */
  isSaving: boolean;
  /** Error message if any */
  error: string | null;
  /** Save as draft */
  saveDraft: () => Promise<BlogPost | null>;
  /** Publish blog post */
  publish: () => Promise<BlogPost | null>;
  /** Delete blog post */
  deleteBlog: (id: string) => Promise<void>;
  /** Reset form to initial state */
  reset: () => void;
}

/**
 * Default blog post data
 */
const defaultBlogData: BlogPostInput = {
  title: '',
  slug: '',
  content: '',
  excerpt: '',
  publishedDate: new Date().toISOString().split('T')[0],
  categories: [],
  tags: [],
  isFeatured: false,
  status: 'draft',
  publishedAt: null,
};

/**
 * Generate slug from title
 */
const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Custom hook for blog post editing
 */
export const useBlogEditor = (
  initialBlog?: BlogPostInput
): UseBlogEditorReturn => {
  const [blog, setBlog] = useState<BlogPostInput>(
    initialBlog || defaultBlogData
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  /**
   * Update a specific field
   */
  const updateField = useCallback(
    <K extends keyof BlogPostInput>(
      field: K,
      value: BlogPostInput[K]
    ) => {
      setBlog((prev) => {
        const updated = { ...prev, [field]: value };
        
        // Auto-generate slug from title if slug is empty
        if (field === 'title' && !updated.slug) {
          updated.slug = generateSlug(value as string);
        }
        
        return updated;
      });
    },
    []
  );

  /**
   * Save blog post as draft
   */
  const saveDraft = useCallback(async (): Promise<BlogPost | null> => {
    setIsSaving(true);
    setError(null);

    try {
      // Validate required fields
      if (!blog.title || !blog.title.trim()) {
        throw new Error('Title is required');
      }
      if (!blog.content || !blog.content.trim()) {
        throw new Error('Content is required');
      }

      const blogData = {
        ...blog,
        status: 'draft' as const,
        publishedAt: null,
        lastModified: new Date().toISOString(),
        // Auto-generate slug if not provided
        slug: blog.slug || generateSlug(blog.title),
      };

      // If blog has an _id, it's an update; otherwise, it's a create
      const blogId = (blog as any)._id;
      let savedBlog: BlogPost;

      if (blogId) {
        savedBlog = await adminBlogService.updateBlog(blogId, blogData);
      } else {
        savedBlog = await adminBlogService.createBlog(blogData);
      }

      return savedBlog;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to save blog post';
      setError(errorMessage);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [blog]);

  /**
   * Publish blog post
   */
  const publish = useCallback(async (): Promise<BlogPost | null> => {
    setIsSaving(true);
    setError(null);

    try {
      // Validate required fields
      if (!blog.title || !blog.title.trim()) {
        throw new Error('Title is required');
      }
      if (!blog.content || !blog.content.trim()) {
        throw new Error('Content is required');
      }
      // Published date is optional - will use current date if not provided

      // If blog has an _id, use publish endpoint; otherwise, create with status='published'
      const blogId = (blog as any)._id;
      let savedBlog: BlogPost;

      if (blogId) {
        // For existing posts, use the dedicated publish endpoint
        savedBlog = await adminBlogService.publishBlog(blogId);
      } else {
        // For new posts, create with status='published'
        const blogData = {
          ...blog,
          status: 'published' as const,
          publishedAt: new Date().toISOString(),
          publishedDate: blog.publishedDate || new Date().toISOString().split('T')[0],
          lastModified: new Date().toISOString(),
          // Auto-generate slug if not provided
          slug: blog.slug || generateSlug(blog.title),
        };
        savedBlog = await adminBlogService.createBlog(blogData);
      }

      return savedBlog;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to publish blog post';
      setError(errorMessage);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [blog]);

  /**
   * Delete blog post
   */
  const deleteBlog = useCallback(
    async (id: string): Promise<void> => {
      setIsSaving(true);
      setError(null);

      try {
        await adminBlogService.deleteBlog(id);
        navigate('/admin');
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to delete blog post';
        setError(errorMessage);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [navigate]
  );

  /**
   * Reset form to initial state
   */
  const reset = useCallback(() => {
    setBlog(initialBlog || defaultBlogData);
    setError(null);
  }, [initialBlog]);

  return {
    blog,
    setBlog,
    updateField,
    isSaving,
    error,
    saveDraft,
    publish,
    deleteBlog,
    reset,
  };
};

